// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type TumblingEGraphType = {
  examData: ExamSectionReportType;
};

const TumblingEGraph = ({ examData }: TumblingEGraphType) => {
  const { t } = useTranslation();

  const displayedDirections = useMemo(() => {
    const directions = examData?.state?.directions ?? [];

    return directions?.flatMap((dir) =>
      dir?.letters?.map((char) => ({
        color:
          char?.State === "GuessedCorrectly"
            ? "#13A321"
            : char?.State === "Skipped"
              ? "#202338"
              : char?.State === "None"
                ? "#202338"
                : "#B21515",
        backgroundImage:
          char?.State === "Skipped"
            ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
            : "none",
        backgroundSize: "8px 8px",
        value: char?.Value,
      }))
    );
  }, [examData?.state?.directions]);

  const getDirChosen = useCallback(
    (value: string) =>
      value === "UP"
        ? "270deg"
        : value === "DOWN"
          ? "90deg"
          : value === "LEFT"
            ? "180deg"
            : value === "RIGHT"
              ? "0deg"
              : null,
    []
  );

  const isError = useMemo(
    () => !examData?.state?.directions || !examData?.state?.directions?.length,
    [examData?.state?.directions]
  );

  return (
    <Grid
      sx={{
        display: "grid",
        gridTemplateColumns: isError ? "repeat(1, 1fr)" : "repeat(6, 1fr)",
        gridGap: { xs: "5px", sm: "10px" },
        maxWidth: { xs: "300px", sm: "450px" },
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {isError ? (
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("Error: No patient responses recorded.")}
        </Typography>
      ) : (
        displayedDirections?.map((letter, index) => {
          const value = letter?.value;
          const directionChosen = getDirChosen(value);

          return (
            <Grid
              item
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transform: `rotate(${directionChosen})`,
                backgroundImage: letter?.backgroundImage,
                backgroundSize: letter?.backgroundSize,
                width: { xs: "40px", sm: "54px" },
                height: { xs: "40px", sm: "54px" },

                marginRight: {
                  xs: index === 2 ? "30px" : "0px",
                  sm: index === 2 ? "60px" : "0px",
                },
              }}
            >
              <Typography
                color={letter?.color}
                fontWeight={600}
                fontSize={{ xs: "20px", sm: "40px" }}
                fontFamily="Roboto, Helvetica, Arial, sans-serif"
              >
                E
              </Typography>
            </Grid>
          );
        })
      )}
    </Grid>
  );
};

export default TumblingEGraph;
